import { KNOWLEDGE_ITEM_TYPES } from '../knowledgeItems/knowledgeItemModel';
import {
  PROFILE_PLACEMENT_CHAPTERS,
  normalizeProfilePlacementChapterId,
} from './profilePlacementMetadata';

const FIELD_CHAPTERS = Object.freeze({
  communicationStyle: PROFILE_PLACEMENT_CHAPTERS.COMMUNICATION,
  communicationNotes: PROFILE_PLACEMENT_CHAPTERS.COMMUNICATION,
  whatHelps: PROFILE_PLACEMENT_CHAPTERS.WHAT_HELPS,
  thingsThatUsuallyHelp: PROFILE_PLACEMENT_CHAPTERS.WHAT_HELPS,
  familySupportTips: PROFILE_PLACEMENT_CHAPTERS.WHAT_HELPS,
  earlyWarningSigns: PROFILE_PLACEMENT_CHAPTERS.WATCH_FOR,
  thingsToAvoid: PROFILE_PLACEMENT_CHAPTERS.WATCH_FOR,
  safetyConsiderations: PROFILE_PLACEMENT_CHAPTERS.SAFETY,
});

const TYPE_CHAPTERS = Object.freeze({
  communication: PROFILE_PLACEMENT_CHAPTERS.COMMUNICATION,
  strategy: PROFILE_PLACEMENT_CHAPTERS.WHAT_HELPS,
  accommodation: PROFILE_PLACEMENT_CHAPTERS.WHAT_HELPS,
  preference: PROFILE_PLACEMENT_CHAPTERS.WHAT_HELPS,
  support: PROFILE_PLACEMENT_CHAPTERS.WHAT_HELPS,
  trigger: PROFILE_PLACEMENT_CHAPTERS.WATCH_FOR,
  behavior: PROFILE_PLACEMENT_CHAPTERS.WATCH_FOR,
  routine: PROFILE_PLACEMENT_CHAPTERS.DAILY_LIFE,
  sleep: PROFILE_PLACEMENT_CHAPTERS.DAILY_LIFE,
  feeding: PROFILE_PLACEMENT_CHAPTERS.DAILY_LIFE,
  safety: PROFILE_PLACEMENT_CHAPTERS.SAFETY,
  risk: PROFILE_PLACEMENT_CHAPTERS.SAFETY,
  medication: PROFILE_PLACEMENT_CHAPTERS.HEALTH_CARE,
  condition: PROFILE_PLACEMENT_CHAPTERS.HEALTH_CARE,
  baseline: PROFILE_PLACEMENT_CHAPTERS.HEALTH_CARE,
});

const cleanText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();
const normalizeLifecycleValue = (value = '') => cleanText(value).toLowerCase();

const getFactValue = (fact = {}) => cleanText(fact.summary || fact.title || fact.content || fact.sourceText);

const getContentChapterId = (fact = {}) => {
  const text = cleanText([fact.title, fact.summary, fact.content, fact.sourceText].filter(Boolean).join(' ')).toLowerCase();
  if (!text) return '';

  if (/\b(?:communicat|speak|speech|words?|phrases?|sentences?|language|understand|respond|answer|question|aac|signs?|gestures?)\b/.test(text)) {
    return PROFILE_PLACEMENT_CHAPTERS.COMMUNICATION;
  }
  if (/\b(?:safety|unsafe|risk|danger|emergency|allerg|chok|seizure|elope|wander|fall|injur)\b/.test(text)) {
    return PROFILE_PLACEMENT_CHAPTERS.SAFETY;
  }
  if (/\b(?:medicat|medicine|dose|diagnos|condition|symptom|clinical|medical|therapy|therapist|pain|health|asthma|seizure|diabetes)\b/.test(text)) {
    return PROFILE_PLACEMENT_CHAPTERS.HEALTH_CARE;
  }
  if (/\b(?:trigger|warning sign|watch for|overwhelm|upset|escalat|distress|meltdown|shutdown|makes? .* harder|avoid)\b/.test(text)) {
    return PROFILE_PLACEMENT_CHAPTERS.WATCH_FOR;
  }
  if (/\b(?:routine|sleep|bedtime|morning|meal|food|feeding|bath|toilet|school day|daily)\b/.test(text)) {
    return PROFILE_PLACEMENT_CHAPTERS.DAILY_LIFE;
  }
  if (/\b(?:helps?|calm|comfort|works? (?:well|best)|support|strategy|accommodation)\b/.test(text)) {
    return PROFILE_PLACEMENT_CHAPTERS.WHAT_HELPS;
  }
  return '';
};

export const isKnowledgeFactLifecycleVisible = (fact = {}) => {
  const status = normalizeLifecycleValue(fact.status);
  const factStatus = normalizeLifecycleValue(fact.factStatus || 'current');
  const reviewState = normalizeLifecycleValue(fact.reviewState);
  const reviewStatus = normalizeLifecycleValue(fact.reviewStatus);

  if (fact.resolverEligible === false) return false;
  if (fact.archived === true) return false;
  if (fact.profileVisibilityOverride === 'hide') return false;
  if (factStatus && factStatus !== 'current') return false;
  if (['archived', 'discarded', 'rejected'].includes(status)) return false;
  if (['discarded', 'rejected'].includes(reviewState)) return false;
  if (['discarded', 'rejected'].includes(reviewStatus)) return false;
  return Boolean(getFactValue(fact));
};

export const resolveKnowledgeFactProfileChapterId = (fact = {}, { includeFallback = true } = {}) => {
  if (!isKnowledgeFactLifecycleVisible(fact)) return '';

  const placementChapterId = normalizeProfilePlacementChapterId(fact.profileChapterId);
  if (placementChapterId && placementChapterId !== PROFILE_PLACEMENT_CHAPTERS.GOALS) {
    return placementChapterId;
  }

  const fieldChapterId = FIELD_CHAPTERS[cleanText(fact.profileField)];
  if (fieldChapterId) return fieldChapterId;

  if (!includeFallback) return '';

  const contentChapterId = getContentChapterId(fact);
  if (contentChapterId) return contentChapterId;

  const type = fact.knowledgeType || fact.factType || fact.bucket || KNOWLEDGE_ITEM_TYPES.OTHER;
  return TYPE_CHAPTERS[type] || PROFILE_PLACEMENT_CHAPTERS.WHAT_HELPS;
};

export const isKnowledgeFactProjectedToChapter = (fact = {}, chapterId = '', options = {}) => (
  resolveKnowledgeFactProfileChapterId(fact, options) === normalizeProfilePlacementChapterId(chapterId)
);

export const resolveKnowledgeFactsForProfileChapter = (facts = [], chapterId = '', options = {}) => (
  (Array.isArray(facts) ? facts : []).filter((fact) => isKnowledgeFactProjectedToChapter(fact, chapterId, options))
);
